/**
 * /my-alerts — manage saved searches that trigger email notifications
 * when a new matching property is listed. Requires sign-in.
 */
import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Bell, ArrowLeft, Trash2, Plus, Pencil, PlayCircle, Loader2, Save,
} from "lucide-react";
import { toast } from "sonner";

import PageShell from "@/components/PageShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { useShopperAuth } from "@/context/ShopperAuthContext";
import { useBackHref } from "@/hooks/useBackHref";
import { getApiUrl } from "@/utils/api";

const API = getApiUrl();

const EMPTY = {
  name: "", cadence: "daily",
  filters: {
    postcode: "", property_type: "", min_price: "", max_price: "",
    min_beds: "", min_baths: "", q: "",
  },
};

const CADENCE_OPTIONS = [
  { value: "instant", label: "Instant (as soon as a new match is listed)" },
  { value: "daily", label: "Daily digest (once a day at 09:00 UTC)" },
  { value: "both", label: "Both instant + daily digest" },
];

function fmtFilters(f = {}) {
  const parts = [];
  if (f.postcode) parts.push(f.postcode.toUpperCase());
  if (f.property_type) parts.push(f.property_type);
  if (f.min_beds) parts.push(`${f.min_beds}+ bed`);
  if (f.min_baths) parts.push(`${f.min_baths}+ bath`);
  if (f.min_price) parts.push(`from £${Number(f.min_price).toLocaleString()}`);
  if (f.max_price) parts.push(`up to £${Number(f.max_price).toLocaleString()}`);
  if (f.q) parts.push(`“${f.q}”`);
  return parts.join(" · ") || "Any property";
}

export default function MyAlertsPage() {
  const { shopper, loading: authLoading, signIn } = useShopperAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState({ open: false, editing: null, form: { ...EMPTY } });
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState({});
  const back = useBackHref("/properties");

  // Auto-open "New alert" pre-filled with `?new=1&postcode=…` params
  // — triggered by the "Save as alert" button on the properties filter bar.
  useEffect(() => {
    if (!shopper) return;
    const p = new URLSearchParams(location.search || "");
    if (p.get("new") !== "1") return;
    setDialog({
      open: true, editing: null,
      form: {
        ...EMPTY,
        filters: {
          ...EMPTY.filters,
          postcode: p.get("postcode") || "",
          property_type: p.get("property_type") || "",
          min_price: p.get("min_price") || "",
          max_price: p.get("max_price") || "",
          min_beds: p.get("min_beds") || "",
          min_baths: p.get("min_baths") || "",
          q: p.get("q") || "",
        },
      },
    });
    // Strip the query string so a refresh doesn't reopen the dialog.
    navigate("/my-alerts", { replace: true });
  }, [shopper, location.search, navigate]);

  const load = async () => {
    if (!shopper) { setLoading(false); return; }
    try {
      const { data } = await axios.get(`${API}/shopper/me/saved-searches`, { withCredentials: true });
      setItems(data.saved_searches || []);
    } catch {
      toast.error("Failed to load alerts");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [shopper]);

  const openNew = () => setDialog({ open: true, editing: null, form: { ...EMPTY, filters: { ...EMPTY.filters } } });
  const openEdit = (a) => setDialog({
    open: true, editing: a,
    form: {
      name: a.name || "",
      cadence: a.cadence || "daily",
      filters: {
        postcode: a.filters?.postcode || "",
        property_type: a.filters?.property_type || "",
        min_price: a.filters?.min_price ?? "",
        max_price: a.filters?.max_price ?? "",
        min_beds: a.filters?.min_beds ?? "",
        min_baths: a.filters?.min_baths ?? "",
        q: a.filters?.q || "",
      },
    },
  });
  const close = () => setDialog({ open: false, editing: null, form: { ...EMPTY } });

  const save = async () => {
    const f = dialog.form.filters;
    const payload = {
      name: dialog.form.name || "",
      cadence: dialog.form.cadence,
      filters: {
        postcode: (f.postcode || "").trim(),
        property_type: (f.property_type || "").trim(),
        min_price: f.min_price === "" ? null : Number(f.min_price),
        max_price: f.max_price === "" ? null : Number(f.max_price),
        min_beds: f.min_beds === "" ? null : Number(f.min_beds),
        min_baths: f.min_baths === "" ? null : Number(f.min_baths),
        q: (f.q || "").trim(),
      },
    };
    setSaving(true);
    try {
      if (dialog.editing) {
        await axios.put(`${API}/shopper/me/saved-searches/${dialog.editing.saved_search_id}`, payload, { withCredentials: true });
        toast.success("Alert updated");
      } else {
        await axios.post(`${API}/shopper/me/saved-searches`, payload, { withCredentials: true });
        toast.success("Alert saved — you'll get an email when a match lists");
      }
      close();
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };
  const remove = async (a) => {
    if (!window.confirm(`Delete alert "${a.name || 'this saved search'}"?`)) return;
    try {
      await axios.delete(`${API}/shopper/me/saved-searches/${a.saved_search_id}`, { withCredentials: true });
      toast.success("Alert deleted");
      load();
    } catch {
      toast.error("Delete failed");
    }
  };
  const testMatches = async (a) => {
    setTesting((t) => ({ ...t, [a.saved_search_id]: true }));
    try {
      const { data } = await axios.post(`${API}/shopper/me/saved-searches/${a.saved_search_id}/test`, {}, { withCredentials: true });
      toast.success(`${data.count} propert${data.count === 1 ? "y" : "ies"} would have matched in the last 24h`);
    } catch {
      toast.error("Test failed");
    } finally {
      setTesting((t) => ({ ...t, [a.saved_search_id]: false }));
    }
  };

  // Auth gate — the page still renders chrome so header/footer stay consistent.
  if (authLoading) {
    return (
      <PageShell chrome={true}>
        <div data-public-site="true" className="min-h-screen flex items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin" />
        </div>
      </PageShell>
    );
  }
  if (!shopper) {
    return (
      <PageShell chrome={true}>
        <div data-public-site="true" className="min-h-screen bg-background">
          <div className="max-w-md mx-auto text-center py-24 px-6" data-testid="alerts-signin-gate">
            <Bell className="h-8 w-8 text-muted-foreground mx-auto mb-3" />
            <h1 className="text-2xl font-serif mb-2">Property Alerts</h1>
            <p className="text-sm text-muted-foreground mb-4">
              Sign in to save your search filters and get an email when a new property matches — instantly or as a daily digest.
            </p>
            <button
              type="button"
              onClick={() => signIn("/my-alerts")}
              className="inline-flex items-center gap-2 h-10 px-4 border border-border rounded-md text-sm font-medium bg-background hover:bg-muted/50 transition-colors mx-auto"
              data-testid="alerts-signin-btn"
            >
              Sign in with Google
            </button>
          </div>
        </div>
      </PageShell>
    );
  }

  const f = dialog.form.filters;
  const setF = (patch) => setDialog((d) => ({ ...d, form: { ...d.form, filters: { ...d.form.filters, ...patch } } }));

  return (
    <PageShell chrome={true}>
      <div data-public-site="true" className="min-h-screen bg-background">
        <div className="max-w-4xl mx-auto px-6 py-10">
          <Link to={back.href} className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-6" data-testid="alerts-back-link">
            <ArrowLeft className="h-3.5 w-3.5" /> {back.label}
          </Link>
          <div className="flex items-start justify-between gap-4 mb-8">
            <div>
              <h1 className="text-3xl font-serif mb-1 flex items-center gap-2">
                <Bell className="h-6 w-6 text-primary" />
                Property Alerts
              </h1>
              <p className="text-sm text-muted-foreground">
                Get emailed the moment (or once a day) when a new property matches your saved filters.
              </p>
            </div>
            <Button onClick={openNew} data-testid="alerts-new-btn">
              <Plus className="h-4 w-4 mr-2" /> New alert
            </Button>
          </div>

          {loading ? (
            <div className="text-center text-sm text-muted-foreground py-12">Loading…</div>
          ) : items.length === 0 ? (
            <div className="text-center border border-dashed border-border rounded-lg p-12" data-testid="alerts-empty">
              <Bell className="h-8 w-8 mx-auto text-muted-foreground mb-3" />
              <p className="text-muted-foreground mb-4">
                No alerts yet. Save a search to be first when a matching home lists.
              </p>
              <Button onClick={openNew}>Create your first alert</Button>
            </div>
          ) : (
            <ul className="space-y-3" data-testid="alerts-list">
              {items.map((a) => (
                <li key={a.saved_search_id} className="border border-border rounded-lg bg-background p-4 flex items-start justify-between gap-4" data-testid={`alert-row-${a.saved_search_id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium mb-1">{a.name}</div>
                    <div className="text-sm text-muted-foreground truncate">{fmtFilters(a.filters)}</div>
                    <div className="mt-2 flex items-center gap-2 text-xs">
                      <span className="inline-flex items-center gap-1 bg-primary/10 text-primary px-2 py-0.5 rounded-full">
                        {a.cadence === "instant" ? "Instant" : a.cadence === "daily" ? "Daily digest" : "Instant + digest"}
                      </span>
                      {a.last_notified_at && (
                        <span className="text-muted-foreground">Last sent {new Date(a.last_notified_at).toLocaleDateString()}</span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Button variant="ghost" size="sm" onClick={() => testMatches(a)} disabled={testing[a.saved_search_id]} data-testid={`alert-test-${a.saved_search_id}`}>
                      {testing[a.saved_search_id] ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <PlayCircle className="h-3.5 w-3.5" />}
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => openEdit(a)} data-testid={`alert-edit-${a.saved_search_id}`}>
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => remove(a)} className="text-red-600 hover:bg-red-50" data-testid={`alert-remove-${a.saved_search_id}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <Dialog open={dialog.open} onOpenChange={(v) => (v ? null : close())}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="alert-dialog">
          <DialogHeader>
            <DialogTitle>{dialog.editing ? "Edit Alert" : "New Alert"}</DialogTitle>
            <DialogDescription>
              Set the filters that describe the property you&apos;re looking for. We&apos;ll email you when a new listing matches.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Name (optional)</Label>
              <Input
                value={dialog.form.name}
                onChange={(e) => setDialog((d) => ({ ...d, form: { ...d.form, name: e.target.value } }))}
                placeholder="e.g. 3-bed near Waterloo under £600k"
                data-testid="alert-form-name"
              />
            </div>
            <div>
              <Label>How often?</Label>
              <select
                value={dialog.form.cadence}
                onChange={(e) => setDialog((d) => ({ ...d, form: { ...d.form, cadence: e.target.value } }))}
                className="w-full h-10 px-3 border border-input bg-background rounded-none text-sm"
                data-testid="alert-form-cadence"
              >
                {CADENCE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            <div className="pt-3 border-t border-border">
              <div className="text-sm font-medium mb-2">Filters</div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Postcode</Label>
                  <Input value={f.postcode} onChange={(e) => setF({ postcode: e.target.value })} placeholder="SW1" data-testid="alert-form-postcode" />
                </div>
                <div>
                  <Label className="text-xs">Property type</Label>
                  <select
                    value={f.property_type}
                    onChange={(e) => setF({ property_type: e.target.value })}
                    className="w-full h-10 px-3 border border-input bg-background rounded-none text-sm"
                    data-testid="alert-form-type"
                  >
                    <option value="">Any</option>
                    <option value="terraced">Terraced</option>
                    <option value="semi-detached">Semi-Detached</option>
                    <option value="detached">Detached</option>
                    <option value="townhouse">Townhouse</option>
                    <option value="flat">Flat</option>
                    <option value="bungalow">Bungalow</option>
                    <option value="land">Land</option>
                  </select>
                </div>
                <div>
                  <Label className="text-xs">Min bedrooms</Label>
                  <Input type="number" min="0" value={f.min_beds} onChange={(e) => setF({ min_beds: e.target.value })} data-testid="alert-form-min-beds" />
                </div>
                <div>
                  <Label className="text-xs">Min bathrooms</Label>
                  <Input type="number" min="0" value={f.min_baths} onChange={(e) => setF({ min_baths: e.target.value })} data-testid="alert-form-min-baths" />
                </div>
                <div>
                  <Label className="text-xs">Min price (£)</Label>
                  <Input type="number" min="0" value={f.min_price} onChange={(e) => setF({ min_price: e.target.value })} data-testid="alert-form-min-price" />
                </div>
                <div>
                  <Label className="text-xs">Max price (£)</Label>
                  <Input type="number" min="0" value={f.max_price} onChange={(e) => setF({ max_price: e.target.value })} data-testid="alert-form-max-price" />
                </div>
                <div className="col-span-2">
                  <Label className="text-xs">Keyword search</Label>
                  <Input value={f.q} onChange={(e) => setF({ q: e.target.value })} placeholder="e.g. garden, garage" data-testid="alert-form-q" />
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={close}>Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="alert-form-save">
              {saving ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Saving…</> : <><Save className="mr-2 h-4 w-4" />{dialog.editing ? "Update" : "Create alert"}</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageShell>
  );
}
